import { nextTick, type MaybeRefOrGetter, type Ref, shallowRef, toValue, watch } from "vue";
import { useDebounceFn, useResizeObserver, useThrottleFn, useWindowSize } from "@vueuse/core";
import type { DSPopoverPlacement } from "../types/props";
import type { Position } from "../types/position";
import { DS_POPOVER_PLACEMENT_TO_ARROW_SIDE } from "../types/arrow";
import { DS_POPOVER_ARROW_OFFSET_VAR } from "../constants/classes";
import { applyViewportConstraints, calculateArrowOffset, calculatePopoverCoordinates } from "../utils/positioning";

interface UsePopoverStylesOptions {
  activatorRef: Ref<HTMLElement | undefined>;
  contentRef: Ref<HTMLElement | undefined>;
  placement: MaybeRefOrGetter<DSPopoverPlacement>;
  offset: MaybeRefOrGetter<number>;
  inheritActivatorWidth: boolean;
  /** Set to false to let the content follow the activator outside the viewport instead of clamping it. */
  constrainToViewport?: MaybeRefOrGetter<boolean>;
  /** Adds the arrow offset custom property to the content styles. */
  withArrow?: MaybeRefOrGetter<boolean>;
  /** Called after the content coordinates change, so consumers can align decorations to the activator. */
  onPositioned?: () => void;
}

type PopoverStyles = Record<string, string>;
const VIEWPORT_PADDING = 8;

export const usePopoverStyles = ({
  activatorRef,
  contentRef,
  placement,
  offset,
  inheritActivatorWidth,
  constrainToViewport = true,
  withArrow = false,
  onPositioned,
}: UsePopoverStylesOptions) => {
  const contentStyles = shallowRef<PopoverStyles>({
    visibility: "hidden",
    position: "fixed",
  });

  const areElementsValid = (): boolean => {
    return !!(activatorRef.value && contentRef.value);
  };

  const getValidRectangles = () => {
    if (!areElementsValid()) return null;

    const activatorRect = activatorRef.value?.getBoundingClientRect();
    const contentRect = contentRef.value!.getBoundingClientRect();

    if (contentRect.width === 0 || contentRect.height === 0) {
      return null;
    }

    return { activatorRect, contentRect };
  };

  const getAvailableWidth = (): number => Math.max(0, window.innerWidth - VIEWPORT_PADDING * 2);

  const getEffectiveContentRect = (
    activatorRect: DOMRect,
    contentRect: DOMRect,
    inheritActivatorWidth: boolean
  ): DOMRect => {
    if (!inheritActivatorWidth) return contentRect;

    const availableWidth = getAvailableWidth();
    const minWidth = Math.min(activatorRect.width, availableWidth);
    const width = Math.min(Math.max(contentRect.width, minWidth), availableWidth);

    return new DOMRect(contentRect.x, contentRect.y, width, contentRect.height);
  };

  const computeConstrainedPositions = (activatorRect: DOMRect, contentRect: DOMRect) => {
    const { top, left } = calculatePopoverCoordinates({
      activatorRect,
      contentRect,
      placement: toValue(placement),
      offset: toValue(offset),
    });

    if (!toValue(constrainToViewport)) return { top, left };

    return applyViewportConstraints({
      position: { top, left },
      contentRect,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      padding: VIEWPORT_PADDING,
    });
  };

  const createStyles = (
    position: { top: number; left: number },
    inheritActivatorWidth: boolean,
    activatorRect: DOMRect
  ): PopoverStyles => {
    const styles: PopoverStyles = {
      top: `${position.top}px`,
      left: `${position.left}px`,
      visibility: contentStyles.value.visibility,
      position: "fixed",
    };

    if (inheritActivatorWidth) {
      const availableWidth = getAvailableWidth();

      styles.minWidth = `${Math.min(activatorRect.width, availableWidth)}px`;
      styles.maxWidth = `${availableWidth}px`;
    }

    return styles;
  };

  const arrowOffsetStyle = (activatorRect: DOMRect, contentRect: DOMRect, position: Position): string => {
    const offset = calculateArrowOffset({
      activatorRect,
      contentRect,
      position,
      side: DS_POPOVER_PLACEMENT_TO_ARROW_SIDE[toValue(placement)],
    });
    return `${offset}px`;
  };

  const shouldUpdateStyles = (newStyles: PopoverStyles): boolean => {
    return JSON.stringify(newStyles) !== JSON.stringify(contentStyles.value);
  };

  const calculateStyles = async () => {
    await nextTick();

    const rectangles = getValidRectangles();
    if (!rectangles?.activatorRect) return;

    const effectiveContentRect = getEffectiveContentRect(
      rectangles.activatorRect,
      rectangles.contentRect,
      inheritActivatorWidth
    );
    const constrainedPosition = computeConstrainedPositions(rectangles.activatorRect, effectiveContentRect);

    const newStyles = createStyles(constrainedPosition, inheritActivatorWidth, rectangles.activatorRect);
    if (toValue(withArrow)) {
      newStyles[DS_POPOVER_ARROW_OFFSET_VAR] = arrowOffsetStyle(
        rectangles.activatorRect,
        effectiveContentRect,
        constrainedPosition
      );
    }

    if (shouldUpdateStyles(newStyles)) {
      contentStyles.value = newStyles;
      onPositioned?.();
    }
  };

  const setVisibility = (visibility: "visible" | "hidden") => {
    contentStyles.value = {
      ...contentStyles.value,
      visibility,
    };
  };

  const isVisible = () => contentStyles.value.visibility === "visible";

  const throttledCalculateStyles = useThrottleFn(calculateStyles, 16, true);
  const debouncedCalculateStyles = useDebounceFn(calculateStyles, 100);

  const handleRestylingIfVisible = async () => {
    if (isVisible()) {
      await debouncedCalculateStyles();
    }
  };

  watch(
    [() => toValue(placement), () => toValue(offset), () => toValue(constrainToViewport), () => toValue(withArrow)],
    () => {
      if (isVisible()) void calculateStyles();
    }
  );

  const { width: windowWidth, height: windowHeight } = useWindowSize();
  watch([windowWidth, windowHeight], handleRestylingIfVisible);

  useResizeObserver(contentRef, handleRestylingIfVisible);
  useResizeObserver(activatorRef, handleRestylingIfVisible);

  return {
    contentStyles,
    calculateStyles: throttledCalculateStyles,
    setVisibility,
    isVisible,
  };
};
